import * as Sentry from '@sentry/vue';
import AiSuggestionsAPI from '../../api/aiSuggestions';
import ConversationAiSuggestionsAPI from '../../api/conversationAiSuggestions';

export const types = {
  SET_UI_FLAG: 'SET_UI_FLAG',
  SET_CONVERSATION_UI_FLAG: 'SET_CONVERSATION_UI_FLAG',
  SET_CONFIG: 'SET_CONFIG',
  SET_AGENT_SETTING: 'SET_AGENT_SETTING',
  SET_AGENTS: 'SET_AGENTS',
  UPDATE_AGENT: 'UPDATE_AGENT',
  SET_PROMPT_VERSIONS: 'SET_PROMPT_VERSIONS',
  SET_SUGGESTIONS: 'SET_SUGGESTIONS',
  UPSERT_SUGGESTION: 'UPSERT_SUGGESTION',
  SET_FEEDBACK: 'SET_FEEDBACK',
  SET_FEEDBACK_SUMMARY: 'SET_FEEDBACK_SUMMARY',
  SET_RATING: 'SET_RATING',
  SETTLE_RATING: 'SETTLE_RATING',
  TICK_SETTINGS: 'TICK_SETTINGS',
  SETTLE_SETTINGS: 'SETTLE_SETTINGS',
  INVALIDATE_SETTINGS: 'INVALIDATE_SETTINGS',
  FAIL_SETTINGS: 'FAIL_SETTINGS',
};

const newestFirst = (a, b) =>
  (b.created_at || 0) - (a.created_at || 0) || b.id - a.id;

const isPreview = suggestion => suggestion?.trigger === 'preview';

// Matches the conversation endpoint: newest five, no previews.
const LIST_LIMIT = 5;

// `revision` grows on every write to the row; `updated_at` is whole seconds
// and cannot order fast updates. Rows without one sort before any revision.
const revisionOf = suggestion =>
  Number.isFinite(suggestion?.revision) ? suggestion.revision : -1;

// Computed per response, and within one revision it only ever turns false
// (the policy revision and the newest patient message only move forward).
// Rows without a revision carry no order, so this needs one on both copies.
const SAME_REVISION_STRICT = ['eligible'];

// REST responses and cable events can arrive out of order; keep the newest.
// The server leaves `draft` out unless the row is eligible or settled, so a
// newer payload without it must not keep a draft from an older copy.
const reconcile = (current, incoming) => {
  if (!current) return incoming;
  if (revisionOf(incoming) < revisionOf(current)) return current;
  const merged = { ...current, ...incoming };
  if (!('draft' in incoming)) merged.draft = null;
  const sameRevision =
    Number.isFinite(incoming.revision) &&
    incoming.revision === current.revision;
  if (sameRevision) {
    SAME_REVISION_STRICT.forEach(key => {
      if (key in current && current[key] !== true) merged[key] = current[key];
    });
  }
  return merged;
};

const sortAndLimit = suggestions =>
  suggestions.sort(newestFirst).slice(0, LIST_LIMIT);

// Reading a conversation's suggestions again (the pending poll, reconnect
// recovery) fails over and over while the server or the network is down.
// After SYNC_FAILURES_TO_REPORT failed reads in a row the conversation is
// flagged `syncFailed`, which the dock shows as a state with Try again; the
// next successful read clears it. The outage is reported once per tab (see
// outageReported).
export const SYNC_FAILURES_TO_REPORT = 3;

// A tab reads up to RESYNC_MAX_PENDING + 1 conversations on every reconnect,
// so one outage would report once per conversation. The first conversation to
// reach SYNC_FAILURES_TO_REPORT reports for the tab, with how many were failing
// then; nothing more is reported until a read works and no conversation is
// failing any more. Per store, out of Vuex state.
const outageReported = new WeakSet();

const failingConversations = $state =>
  Object.values($state?.conversationUiFlags || {}).filter(
    flags => flags?.syncFailures > 0
  ).length;

// Reconnect recovery reads the open conversation at once, then at most
// RESYNC_MAX_PENDING other conversations with a pending row (newest first),
// RESYNC_CONCURRENCY at a time, after a random wait of up to RESYNC_SPREAD_MS
// so tabs that reconnect together after an outage do not read together.
export const RESYNC_MAX_PENDING = 10;
export const RESYNC_CONCURRENCY = 2;
export const RESYNC_SPREAD_MS = 3000;

// Runs `run` on every item, at most `size` at a time, and settles like
// Promise.allSettled, in item order.
const settledInPool = (items, size, run) => {
  const results = [];
  let next = 0;
  const worker = () => {
    if (next >= items.length) return Promise.resolve();
    const index = next;
    next += 1;
    return Promise.allSettled([run(items[index])]).then(([result]) => {
      results[index] = result;
      return worker();
    });
  };
  const workers = Math.min(size, items.length);
  return Promise.all(Array.from({ length: workers }, worker)).then(
    () => results
  );
};

const spreadDelay = () =>
  new Promise(resolve => {
    setTimeout(resolve, Math.floor(Math.random() * RESYNC_SPREAD_MS));
  });

// A settings read that fails during reconnect recovery is tried again after
// each of these waits, jittered between half and all of it, until one works.
// Until then the settings stay stale (isSettingsStale) and the dock does not
// trust them; after the last try they stay stale until the next settings
// event, reconnect or successful read.
export const SETTINGS_RETRY_DELAYS_MS = [2000, 4000, 8000];

const SETTINGS_READS = {
  config: 'fetchConfig',
  agentSetting: 'fetchAgentSetting',
};

// One reconnect recovery at a time per store. A reconnect while one runs (the
// socket dropped again) runs it once more afterwards, since reads started
// before the later outage may have missed its events.
const recoveries = new WeakMap();

// Settings reads and writes (the account config, this nurse's AI mode) can
// resolve in any order, and a settings event means anything read before it is
// out of date. Every request takes a ticket from its kind's clock. A read is
// kept only when no event, saved write or newer read landed since it started;
// a write is kept unless an event or a newer write landed, and makes earlier
// reads stale. So a late GET cannot bring back what an administrator just
// turned off.
const SETTINGS_MUTATIONS = {
  config: types.SET_CONFIG,
  agentSetting: types.SET_AGENT_SETTING,
};

const settingsSyncOf = ($state, kind) => {
  if (!$state.settingsSync) $state.settingsSync = {};
  if (!$state.settingsSync[kind]) {
    $state.settingsSync[kind] = {
      clock: 0,
      eventAt: 0,
      writtenAt: 0,
      read: 0,
      written: 0,
      // See FAIL_SETTINGS.
      stale: false,
    };
  }
  return $state.settingsSync[kind];
};

const isSettingsCurrent = ($state, kind, ticket, write) => {
  const sync = $state?.settingsSync?.[kind];
  if (!sync || ticket === undefined) return true;
  if (ticket <= sync.eventAt) return false;
  if (write) return ticket > sync.written;
  return ticket > sync.writtenAt && ticket > sync.read;
};

// The store holds a response from a request that started after the last
// settings event, so what it holds is the server's current answer.
const isSettingsFresh = ($state, kind) => {
  const sync = $state?.settingsSync?.[kind];
  return !sync || Math.max(sync.read, sync.written) > sync.eventAt;
};

// What is in flight for each kind, per store; promises and callbacks stay out
// of Vuex state. `inFlight` holds request tickets, `failure` the newest
// failed request, and `waiters` the requests waiting for fresh settings,
// woken whenever a request of that kind settles.
const settingsWork = new WeakMap();
const settingsWorkOf = ($state, kind) => {
  if (!$state) return null;
  if (!settingsWork.has($state)) settingsWork.set($state, {});
  const byKind = settingsWork.get($state);
  if (!byKind[kind]) {
    byKind[kind] = { inFlight: new Set(), failure: null, waiters: new Set() };
  }
  return byKind[kind];
};

const settleWork = (work, ticket, error) => {
  if (!work) return;
  work.inFlight.delete(ticket);
  if (error && !(work.failure?.ticket > ticket)) {
    work.failure = { ticket, error };
  }
  [...work.waiters].forEach(wake => wake());
};

// A request whose answer was dropped resolves once the store is fresh again,
// with what it then holds: a settings event's own read, or any newer read or
// save that landed first. Every settled request of the kind wakes it to look
// again, so one stalled read cannot hold it while a newer one has landed.
// When nothing started since the last event is still in flight, it rejects
// with the newest of those requests' errors instead of out of date state.
// Settings reads time out (see api/aiSuggestions), so the wait is bounded.
const freshSettings = ($state, kind) =>
  new Promise((resolve, reject) => {
    const work = settingsWorkOf($state, kind);
    const check = () => {
      const { eventAt } = $state.settingsSync[kind];
      if (isSettingsFresh($state, kind)) {
        resolve($state[kind]);
      } else if ([...work.inFlight].some(ticket => ticket > eventAt)) {
        return false;
      } else {
        reject(
          work.failure?.ticket > eventAt
            ? work.failure.error
            : new Error(`No current ${kind} settings`)
        );
      }
      work.waiters.delete(check);
      return true;
    };
    if (!check()) work.waiters.add(check);
  });

// The server took the save, but the settings event that overtook it could not
// be read again, so the store still holds what it had before the save.
const savedButNotRead = cause =>
  Object.assign(new Error('Settings saved but not read again'), {
    saved: true,
    cause,
  });

// Resolves with the settings the store holds once this request is settled:
// its own response when kept, else the current answer (see freshSettings).
// A successful save whose answer was dropped therefore resolves after the
// settings event's read, never with the state from before the save; when
// that read fails, it rejects with `saved: true`.
const settingsRequest = async (
  { commit, state: $state },
  kind,
  request,
  { write = false } = {}
) => {
  commit(types.TICK_SETTINGS, { kind });
  const ticket = $state?.settingsSync?.[kind]?.clock;
  const work = settingsWorkOf($state, kind);
  work?.inFlight.add(ticket);
  let data;
  try {
    ({ data } = await request());
  } catch (error) {
    if (!write) commit(types.FAIL_SETTINGS, { kind, ticket });
    settleWork(work, ticket, error);
    // A failed save always reports; a failed read that was already replaced
    // waits for its replacement.
    if (write || isSettingsCurrent($state, kind, ticket, write)) throw error;
    return freshSettings($state, kind);
  }
  if (!isSettingsCurrent($state, kind, ticket, write)) {
    settleWork(work, ticket);
    if (!write) return freshSettings($state, kind);
    return freshSettings($state, kind).catch(error => {
      throw savedButNotRead(error);
    });
  }
  commit(types.SETTLE_SETTINGS, { kind, ticket, write });
  commit(SETTINGS_MUTATIONS[kind], data);
  settleWork(work, ticket);
  return data;
};

// Asked for at least once on this page, loaded or still in flight.
const isSettingsRequested = ($state, kind) =>
  !!$state[kind] || ($state.settingsSync?.[kind]?.clock || 0) > 0;

export const state = {
  config: null,
  agentSetting: null,
  // Request clocks for `config` and `agentSetting`, see settingsRequest.
  settingsSync: {},
  agents: [],
  promptVersions: [],
  // Keyed by conversation display id, newest suggestion first.
  byConversation: {},
  // Upsert clock: a list fetch keeps rows upserted after it started.
  syncCounter: 0,
  upsertedAt: {},
  conversationUiFlags: {},
  feedback: { records: [], meta: {} },
  feedbackSummary: null,
  // Keyed by conversation display id: `{ eligible, rating, question }` for
  // the current user, plus `stopped` once the question no longer applies.
  ratings: {},
  // Per conversation write clock: a lookup drops its result when an answer,
  // a skip or a stop landed while it was in flight.
  ratingWrites: {},
  uiFlags: {
    isFetchingConfig: false,
    isUpdatingConfig: false,
    isFetchingAgentSetting: false,
    isUpdatingAgentSetting: false,
    isFetchingAgents: false,
    isFetchingPromptVersions: false,
    isRestoringPromptVersion: false,
    isFetchingFeedback: false,
    isFetchingFeedbackSummary: false,
    isPreviewing: false,
  },
};

export const getters = {
  getUIFlags: $state => $state.uiFlags,
  getConfig: $state => $state.config,
  getAgentSetting: $state => $state.agentSetting,
  isAiModeEnabled: $state => !!$state.agentSetting?.enabled,
  // The config or AI mode the store holds is known to be out of date: a
  // settings event or reconnect said so and the read after it failed. A read
  // still in flight does not count.
  isSettingsStale: $state => kind => !!$state.settingsSync?.[kind]?.stale,
  getAgents: $state => $state.agents,
  getPromptVersions: $state => $state.promptVersions,
  getSuggestions: $state => conversationId =>
    $state.byConversation[conversationId] || [],
  getLatestSuggestion: $state => conversationId =>
    ($state.byConversation[conversationId] || [])[0] || null,
  getConversationUIFlags: $state => conversationId =>
    $state.conversationUiFlags[conversationId] || {},
  getFeedbackRecords: $state => $state.feedback.records,
  getFeedbackMeta: $state => $state.feedback.meta,
  getFeedbackSummary: $state => $state.feedbackSummary,
  getRating: $state => conversationId =>
    ($state.ratings || {})[conversationId] || null,
};

const withFlag = async (commit, flag, request) => {
  commit(types.SET_UI_FLAG, { [flag]: true });
  try {
    return await request();
  } finally {
    commit(types.SET_UI_FLAG, { [flag]: false });
  }
};

const withConversationFlag = async (commit, conversationId, flag, request) => {
  commit(types.SET_CONVERSATION_UI_FLAG, { conversationId, [flag]: true });
  try {
    return await request();
  } finally {
    commit(types.SET_CONVERSATION_UI_FLAG, { conversationId, [flag]: false });
  }
};

const upsertResponse = (commit, data) => {
  commit(types.UPSERT_SUGGESTION, data);
  return data;
};

export const actions = {
  fetchConfig(context) {
    return withFlag(context.commit, 'isFetchingConfig', () =>
      settingsRequest(context, 'config', () => AiSuggestionsAPI.getConfig())
    );
  },

  updateConfig(context, payload) {
    return withFlag(context.commit, 'isUpdatingConfig', () =>
      settingsRequest(
        context,
        'config',
        () => AiSuggestionsAPI.updateConfig(payload),
        { write: true }
      )
    );
  },

  fetchAgentSetting(context) {
    return withFlag(context.commit, 'isFetchingAgentSetting', () =>
      settingsRequest(context, 'agentSetting', () =>
        AiSuggestionsAPI.getAgentSetting()
      )
    );
  },

  updateAgentSetting(context, { active }) {
    return withFlag(context.commit, 'isUpdatingAgentSetting', () =>
      settingsRequest(
        context,
        'agentSetting',
        () => AiSuggestionsAPI.updateAgentSetting({ active }),
        { write: true }
      )
    );
  },

  fetchAgents({ commit }) {
    return withFlag(commit, 'isFetchingAgents', async () => {
      const { data } = await AiSuggestionsAPI.getAgents();
      commit(types.SET_AGENTS, data.payload || []);
      return data.payload;
    });
  },

  async updateAgent({ commit }, { userId, allowed }) {
    const { data } = await AiSuggestionsAPI.updateAgent(userId, { allowed });
    commit(types.UPDATE_AGENT, { user_id: userId, allowed, ...data });
    return data;
  },

  fetchPromptVersions({ commit }) {
    return withFlag(commit, 'isFetchingPromptVersions', async () => {
      const { data } = await AiSuggestionsAPI.getPromptVersions();
      commit(types.SET_PROMPT_VERSIONS, data.payload || []);
      return data.payload;
    });
  },

  restorePromptVersion(context, id) {
    return withFlag(context.commit, 'isRestoringPromptVersion', async () => {
      let data;
      try {
        data = await settingsRequest(
          context,
          'config',
          () => AiSuggestionsAPI.restorePromptVersion(id),
          { write: true }
        );
      } catch (error) {
        if (!error.saved) throw error;
        // Restored but not read again: the history is still read, so it marks
        // the restored version live and does not offer to restore it again.
        await context.dispatch('fetchPromptVersions').catch(() => {});
        // A settings read that landed meanwhile holds the restore; there is
        // nothing left to report as not loaded.
        if (!isSettingsFresh(context.state, 'config')) throw error;
        return context.state.config;
      }
      await context.dispatch('fetchPromptVersions');
      return data;
    });
  },

  fetchFeedback({ commit }, params = {}) {
    return withFlag(commit, 'isFetchingFeedback', async () => {
      const { data } = await AiSuggestionsAPI.getFeedback(params);
      commit(types.SET_FEEDBACK, data);
      return data;
    });
  },

  fetchFeedbackSummary({ commit }, params = {}) {
    return withFlag(commit, 'isFetchingFeedbackSummary', async () => {
      const { data } = await AiSuggestionsAPI.getFeedbackSummary(params);
      commit(types.SET_FEEDBACK_SUMMARY, data);
      return data;
    });
  },

  // Preview runs are audit-only, so the result is returned, never stored.
  preview({ commit }, payload) {
    return withFlag(commit, 'isPreviewing', async () => {
      const { data } = await AiSuggestionsAPI.preview(payload);
      return data;
    });
  },

  // A read already in flight for the conversation is not started again. It
  // may have left before a change the caller has to see, so `{
  // conversationId, force: true }` reads anyway (Restore checks its draft with
  // it), leaving `isFetching` to the read in flight.
  async fetchSuggestions(
    { commit, state: $state, getters: $getters },
    payload
  ) {
    const { conversationId, force = false } =
      payload && typeof payload === 'object'
        ? payload
        : { conversationId: payload };
    if (!conversationId) return null;
    const read = async () => {
      const since = $state?.syncCounter || 0;
      const flagsNow = () => $getters.getConversationUIFlags(conversationId);
      let data;
      try {
        ({ data } = await ConversationAiSuggestionsAPI.get(conversationId));
      } catch (error) {
        const syncFailures = (flagsNow().syncFailures || 0) + 1;
        commit(types.SET_CONVERSATION_UI_FLAG, {
          conversationId,
          syncFailures,
          syncFailed: syncFailures >= SYNC_FAILURES_TO_REPORT,
        });
        if (
          $state &&
          syncFailures >= SYNC_FAILURES_TO_REPORT &&
          !outageReported.has($state)
        ) {
          outageReported.add($state);
          // A count only, no conversation or patient data.
          Sentry.captureException(error, {
            tags: { feature: 'ai_suggestions', step: 'read_suggestions' },
            extra: { affected_conversations: failingConversations($state) },
          });
        }
        throw error;
      }
      const suggestions = data.payload || [];
      commit(types.SET_SUGGESTIONS, { conversationId, suggestions, since });
      if (flagsNow().syncFailures) {
        commit(types.SET_CONVERSATION_UI_FLAG, {
          conversationId,
          syncFailures: 0,
          syncFailed: false,
        });
      }
      if ($state && !failingConversations($state)) {
        outageReported.delete($state);
      }
      return suggestions;
    };
    if (force) return read();
    if ($getters.getConversationUIFlags(conversationId).isFetching) return null;
    return withConversationFlag(commit, conversationId, 'isFetching', read);
  },

  // After the socket reconnects: reads again the open conversation and the
  // conversations with a pending row, whose ready broadcast may have been
  // lost. Bounded so that every tab reconnecting at once after an outage does
  // not flood the server (see RESYNC_*). A conversation already being read is
  // skipped (fetchSuggestions). Settles like Promise.allSettled, the open
  // conversation first.
  async resyncSuggestions({ dispatch, state: $state }, openConversationId) {
    const isOpen = id =>
      !!openConversationId && String(id) === String(openConversationId);
    const pending = Object.values($state.byConversation || {})
      .map(rows => rows.find(row => row.status === 'pending'))
      .filter(row => row && !isOpen(row.conversation_id))
      .sort(newestFirst)
      .slice(0, RESYNC_MAX_PENDING)
      .map(row => row.conversation_id);
    const open = Promise.allSettled(
      openConversationId
        ? [dispatch('fetchSuggestions', openConversationId)]
        : []
    );
    const others = pending.length
      ? spreadDelay().then(() =>
          settledInPool(pending, RESYNC_CONCURRENCY, id =>
            dispatch('fetchSuggestions', id)
          )
        )
      : Promise.resolve([]);
    const [openResults, otherResults] = await Promise.all([open, others]);
    return [...openResults, ...otherResults];
  },

  // After the socket reconnects (ai-docs/ai-suggestions.md, "Missed
  // broadcasts"). A settings event sent while the socket was down is not
  // replayed, so the config and this nurse's AI mode are read again first, the
  // way a settings event reads them (a read started before is dropped if it
  // lands later); then the suggestions, when AI mode is effective. A settings
  // read that failed leaves the settings stale and is tried again
  // (SETTINGS_RETRY_DELAYS_MS) while the suggestions are read with the AI mode
  // the store holds; reading them is harmless, and the dock does not trust
  // stale settings.
  recoverAfterReconnect(context, openConversationId) {
    const { state: $state } = context;
    const current = recoveries.get($state);
    if (current) {
      current.again = true;
      current.openConversationId = openConversationId;
      current.wake?.();
      return current.promise;
    }
    const entry = { again: false, openConversationId, wake: null };
    // A wait that a newer reconnect cuts short.
    const pause = ms =>
      new Promise(resolve => {
        let timer = null;
        const done = () => {
          clearTimeout(timer);
          entry.wake = null;
          resolve();
        };
        timer = setTimeout(done, ms);
        entry.wake = done;
      });
    const eventAtOf = kind => $state?.settingsSync?.[kind]?.eventAt;
    // Stops early once a read works, a newer reconnect starts over, a newer
    // settings event reads the kind itself, or another read made it fresh.
    const retrySettings = async read => {
      const since = {};
      let failed = Object.keys(read || {}).filter(kind => !read[kind]);
      failed.forEach(kind => {
        since[kind] = eventAtOf(kind);
      });
      for (
        let attempt = 0;
        attempt < SETTINGS_RETRY_DELAYS_MS.length && failed.length;
        attempt += 1
      ) {
        const delay = SETTINGS_RETRY_DELAYS_MS[attempt];
        // eslint-disable-next-line no-await-in-loop
        await pause(Math.round(delay / 2 + (Math.random() * delay) / 2));
        failed = failed.filter(
          kind =>
            !entry.again &&
            eventAtOf(kind) === since[kind] &&
            !isSettingsFresh($state, kind)
        );
        // eslint-disable-next-line no-await-in-loop
        const results = await Promise.allSettled(
          failed.map(kind => context.dispatch(SETTINGS_READS[kind]))
        );
        failed = failed.filter(
          (_, index) => results[index].status !== 'fulfilled'
        );
      }
    };
    const run = async () => {
      const read = await context.dispatch('refreshSettingsFromCable', {
        scope: 'config',
      });
      const suggestions = context.getters.isAiModeEnabled
        ? context.dispatch('resyncSuggestions', entry.openConversationId)
        : Promise.resolve([]);
      const [results] = await Promise.all([suggestions, retrySettings(read)]);
      return results;
    };
    const loop = async () => {
      let results = await run();
      while (entry.again) {
        entry.again = false;
        // eslint-disable-next-line no-await-in-loop
        results = await run();
      }
      return results;
    };
    entry.promise = loop().finally(() => recoveries.delete($state));
    recoveries.set($state, entry);
    return entry.promise;
  },

  // A conversation id, or `{ conversationId, channelType }` with the channel
  // the reply box has selected.
  requestSuggestion({ commit }, payload) {
    const { conversationId, channelType } =
      payload && typeof payload === 'object'
        ? payload
        : { conversationId: payload };
    return withConversationFlag(
      commit,
      conversationId,
      'isRequesting',
      async () => {
        const { data } = await ConversationAiSuggestionsAPI.create(
          conversationId,
          { channelType }
        );
        return upsertResponse(commit, data);
      }
    );
  },

  applySuggestion({ commit }, { conversationId, id, text, channelType }) {
    return withConversationFlag(
      commit,
      conversationId,
      'isActing',
      async () => {
        const { data } = await ConversationAiSuggestionsAPI.apply(
          conversationId,
          id,
          { text, channelType }
        );
        return upsertResponse(commit, data);
      }
    );
  },

  rejectSuggestion({ commit }, { conversationId, id }) {
    return withConversationFlag(
      commit,
      conversationId,
      'isActing',
      async () => {
        const { data } = await ConversationAiSuggestionsAPI.reject(
          conversationId,
          id
        );
        return upsertResponse(commit, data);
      }
    );
  },

  // The optional reason after Not useful.
  async setRejectReason({ commit }, { conversationId, id, reason }) {
    const { data } = await ConversationAiSuggestionsAPI.setReason(
      conversationId,
      id,
      { reason }
    );
    return upsertResponse(commit, data);
  },

  dismissSuggestion({ commit }, { conversationId, id }) {
    return withConversationFlag(
      commit,
      conversationId,
      'isActing',
      async () => {
        const { data } = await ConversationAiSuggestionsAPI.dismiss(
          conversationId,
          id
        );
        return upsertResponse(commit, data);
      }
    );
  },

  async fetchRating({ commit, state: $state }, conversationId) {
    const writesOf = () => $state?.ratingWrites?.[conversationId] || 0;
    const since = writesOf();
    const { data } =
      await ConversationAiSuggestionsAPI.getRating(conversationId);
    // Read before the write, it would bring the question back.
    if (writesOf() !== since) return null;
    commit(types.SET_RATING, { conversationId, rating: data });
    return data;
  },

  // An answer or a skip; either way this nurse is not asked again.
  submitRating({ commit, getters: $getters }, { conversationId, payload }) {
    return withConversationFlag(
      commit,
      conversationId,
      'isRating',
      async () => {
        const { data } = await ConversationAiSuggestionsAPI.saveRating(
          conversationId,
          payload
        );
        commit(types.SETTLE_RATING, {
          conversationId,
          rating: {
            ...$getters.getRating(conversationId),
            eligible: false,
            rating: data?.rating ?? data,
          },
        });
        return data;
      }
    );
  },

  // The question no longer applies (rating switched off, no shown draft):
  // stored as stopped, so no trigger asks it again on this page.
  stopAskingRating({ commit, getters: $getters }, conversationId) {
    commit(types.SETTLE_RATING, {
      conversationId,
      rating: {
        ...$getters.getRating(conversationId),
        eligible: false,
        stopped: true,
      },
    });
  },

  // ActionCable `ai_suggestion.updated`. The pubsub token spans every account
  // the user belongs to, and display ids repeat across accounts.
  // An administrator changed the account config or this user's AI mode. What
  // this tab asked for, loaded or still loading, is read again, and any read
  // in flight is dropped; the next screen that needs the rest fetches it.
  // Effective AI mode depends on the config's switch, so a config change reads
  // both. Resolves with whether each read worked, by kind (`{ config: true,
  // agentSetting: false }`); a kind that was not read is left out.
  async refreshSettingsFromCable(
    { commit, dispatch, state: $state, rootGetters },
    data
  ) {
    const currentAccountId = rootGetters.getCurrentAccountId;
    if (
      data?.account_id &&
      currentAccountId &&
      Number(data.account_id) !== Number(currentAccountId)
    ) {
      return {};
    }
    const scopes = { config: data?.scope === 'config', agentSetting: true };
    const kinds = Object.keys(SETTINGS_READS).filter(
      kind => scopes[kind] && isSettingsRequested($state, kind)
    );
    const results = await Promise.allSettled(
      kinds.map(kind => {
        commit(types.INVALIDATE_SETTINGS, { kind });
        return dispatch(SETTINGS_READS[kind]);
      })
    );
    return Object.fromEntries(
      kinds.map((kind, index) => [kind, results[index].status === 'fulfilled'])
    );
  },

  upsertFromCable({ commit, rootGetters }, data) {
    const currentAccountId = rootGetters.getCurrentAccountId;
    if (
      data?.account_id &&
      currentAccountId &&
      Number(data.account_id) !== Number(currentAccountId)
    ) {
      return;
    }
    commit(types.UPSERT_SUGGESTION, data);
  },
};

export const mutations = {
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_CONVERSATION_UI_FLAG]($state, { conversationId, ...flags }) {
    $state.conversationUiFlags = {
      ...$state.conversationUiFlags,
      [conversationId]: {
        ...$state.conversationUiFlags[conversationId],
        ...flags,
      },
    };
  },
  [types.SET_CONFIG]($state, config) {
    $state.config = config;
  },
  [types.SET_AGENT_SETTING]($state, agentSetting) {
    $state.agentSetting = agentSetting;
  },
  [types.TICK_SETTINGS]($state, { kind }) {
    settingsSyncOf($state, kind).clock += 1;
  },
  [types.SETTLE_SETTINGS]($state, { kind, ticket, write }) {
    const sync = settingsSyncOf($state, kind);
    // Kept, so started after the last event: the store is current again.
    sync.stale = false;
    if (write) {
      sync.written = ticket;
      sync.clock += 1;
      sync.writtenAt = sync.clock;
    } else {
      sync.read = ticket;
    }
  },
  [types.INVALIDATE_SETTINGS]($state, { kind }) {
    const sync = settingsSyncOf($state, kind);
    sync.clock += 1;
    sync.eventAt = sync.clock;
  },
  // A read started after a settings event failed and nothing read or saved
  // since that event landed: what the store holds is known to be out of date.
  // It stays so through later events, until a read or save is kept
  // (SETTLE_SETTINGS), so a new event does not make the old settings look
  // trusted again while its read is in flight.
  [types.FAIL_SETTINGS]($state, { kind, ticket }) {
    const sync = settingsSyncOf($state, kind);
    if (
      sync.eventAt &&
      ticket > sync.eventAt &&
      !isSettingsFresh($state, kind)
    ) {
      sync.stale = true;
    }
  },
  [types.SET_AGENTS]($state, agents) {
    $state.agents = agents;
  },
  [types.UPDATE_AGENT]($state, agent) {
    $state.agents = $state.agents.map(item =>
      item.user_id === agent.user_id ? { ...item, ...agent } : item
    );
  },
  [types.SET_PROMPT_VERSIONS]($state, versions) {
    $state.promptVersions = versions;
  },
  // `since` is the upsert clock when the fetch started: rows upserted after
  // it (cable, action responses) survive even when the list does not have
  // them yet. Without `since` the list replaces what is stored.
  [types.SET_SUGGESTIONS]($state, { conversationId, suggestions, since }) {
    const current = $state.byConversation[conversationId] || [];
    const upsertedAt = $state.upsertedAt || {};
    const fetched = suggestions
      .filter(suggestion => !isPreview(suggestion))
      .map(suggestion =>
        reconcile(
          current.find(item => item.id === suggestion.id),
          suggestion
        )
      );
    const newer = current.filter(
      item =>
        since !== undefined &&
        (upsertedAt[item.id] || 0) > since &&
        !fetched.some(row => row.id === item.id)
    );
    $state.byConversation = {
      ...$state.byConversation,
      [conversationId]: sortAndLimit([...fetched, ...newer]),
    };
  },
  [types.UPSERT_SUGGESTION]($state, suggestion) {
    if (!suggestion?.id || !suggestion.conversation_id) return;
    if (isPreview(suggestion)) return;
    const conversationId = suggestion.conversation_id;
    const current = $state.byConversation[conversationId] || [];
    const existing = current.find(item => item.id === suggestion.id);
    const merged = reconcile(existing, suggestion);
    const next = existing
      ? current.map(item => (item.id === suggestion.id ? merged : item))
      : [...current, merged];
    $state.syncCounter = ($state.syncCounter || 0) + 1;
    $state.upsertedAt = {
      ...$state.upsertedAt,
      [suggestion.id]: $state.syncCounter,
    };
    $state.byConversation = {
      ...$state.byConversation,
      [conversationId]: sortAndLimit(next),
    };
  },
  [types.SET_FEEDBACK]($state, { payload = [], meta = {} } = {}) {
    $state.feedback = { records: payload, meta };
  },
  [types.SET_FEEDBACK_SUMMARY]($state, summary) {
    $state.feedbackSummary = summary;
  },
  [types.SET_RATING]($state, { conversationId, rating }) {
    $state.ratings = { ...$state.ratings, [conversationId]: rating };
  },
  [types.SETTLE_RATING]($state, { conversationId, rating }) {
    $state.ratings = { ...$state.ratings, [conversationId]: rating };
    $state.ratingWrites = {
      ...$state.ratingWrites,
      [conversationId]: ($state.ratingWrites?.[conversationId] || 0) + 1,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
